/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

/**
 *
 * Token Service class based on OAuth login
 *
 * In OAuth 2.0 two tokens exists.
 * AccessToken = Used to make direct calls to the bosh rest service.
 * RefreshToken = Used to fetch a accessToken at the start of the app.
 *
 * The accessToken is NOT stored on the client. Only the
 * refreshToken is.
 * At every login we check if a refreshToken is available. If so we ask the server
 * for a new accessToken. The server response contains also a
 * NEW refreshToken which we need to store since the old one is invalid after use.
 *
 * The token service uses the reseller config from the reseller endpoint.
  * The refreshtoken will be stored in the AsyncStorage or keystore depending on device.
 *
 * We initiate the token session via code in the url.
 *
 * If no parameter is supplied, we check for a token
 * in the local storage. If no token is available, the user hasn't
 * start a token validation and a demo token is returned
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// Internal dependencies
import {
  OAUTH_KEYCHAIN_NAME,
  OAUTH_DEFAULT_EXPIRE_SECONDS,
  OAUTH_EXPIRE_THRESHOLD
} from "./o-auth.constants";
import { OAuthError } from "./o-auth.util";


// -----------------------------------------------------------------------------
// CONFIGURTATION
// -----------------------------------------------------------------------------

// Tag for log output etc.
const TAG = '[service/oAuth]';

// -----------------------------------------------------------------------------
// SERVICE REGISTRATION
// -----------------------------------------------------------------------------

//service config for service locator
export const config = ["oAuth", ["localConfig", "fetch", "keyChain?", "logger", "netInfo", "remoteConfig", "queryString", "sessionStorage?"], createOAuthService];

// -----------------------------------------------------------------------------
// PRIVATE MODULE METHODS
// -----------------------------------------------------------------------------

/**
 * Builds a demo json web token
 */
function getDemoToken(rConfig, logger) {

  //lets check if we have multiple "demo tokens"
  if (rConfig.STORE_SHELF_TOKENS) {
    let shelfTokens;
    try {
      shelfTokens = JSON.parse(rConfig.STORE_SHELF_TOKENS)
    } catch (e) {
      logger.warn(`${TAG} Invalid STORE_SHELF_TOKENS in Remote Config`, e);
    }

    if (shelfTokens?.default) {
      return {
        access_token: shelfTokens.default,
        expires_in: Infinity,
        scope: "SCOPE_BOSH",
        token_type: "bearer",
        isDemo: true
      };
    }
  }

  return {
    access_token: rConfig.OAUTH_TOKEN_DEMO_USER,
    expires_in: Infinity,
    scope: "SCOPE_BOSH",
    token_type: "bearer",
    isDemo: true
  };
}


function requestAuthentication(fetch, formData, url) {

  return fetch({
    url,
    options: {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      responeType: "json"
    },
    data: formData,
    timeout: 10000
  })
    .catch(error => new OAuthError(error?.status));
}


/**
* multiple resellers utilize different special parameters.
* These parameters are prefixed with OAUTH_X_ in the reseller config
*/
function addSpecialConfig(config, params) {
  var keys = Object.keys(config);
  var regexp = new RegExp("OAUTH_X_(.*)_KEY");
  for (var i = 0; i < keys.length; i++) {
    if (keys[i].match(regexp)) {
      params.push(
        config["OAUTH_X_" + RegExp.$1 + "_KEY"] +
        "=" +
        config["OAUTH_X_" + RegExp.$1 + "_VALUE"]
      );
    }
  }

  return params;
}


/**
 * Fetches an access token from the Oauth
 * server if a valid grant code is available.
 */
function fetchTokenFromCode(code, fetch, rConfig, isDevelop) {
  if (!code) {
    return Promise.reject(new OAuthError(2));
  }

  // Set authorization options
  let form = [
    "client_id=" + encodeURIComponent(rConfig.OAUTH_CLIENT_ID),
    "grant_type=authorization_code",
    "code=" + encodeURIComponent(code),
    "scope=" + encodeURIComponent(rConfig.OAUTH_SCOPE),
    "redirect_uri=" + encodeURIComponent(isDevelop ? rConfig.URL_OAUTH_REDIRECT : rConfig.URL_OAUTH_AUTHAPP)
  ];

  form = addSpecialConfig(rConfig, form);

  return requestAuthentication(fetch, form.join("&"), rConfig.URL_OAUTH_ACCESSTOKEN);
}


/**
* Sends the refreshtoken which was stored in the storage
* to the oauth server and updates the stored tokens
* Notes:
*  The naming convention in OAuth2.0 is different from OAuth1.x
*  The access token is short living and can not be used twice (e.g. restart of the app)
*  The refresh token is used a every login to get a new access token
*  The refresh token also needs to be exchanged at every login
*/
function fetchTokenFromRefresh(config, fetch, rToken) {

  if (!rToken) {
    return Promise.reject(new OAuthError(3));
  }

  // Set authorization options
  var form = [
    "client_id=" + encodeURIComponent(config.OAUTH_CLIENT_ID),
    "grant_type=refresh_token",
    "refresh_token=" + encodeURIComponent(rToken),
    "scope=" + encodeURIComponent(config.OAUTH_SCOPE)
  ].join("&");

  return requestAuthentication(fetch, form, config.URL_OAUTH_ACCESSTOKEN);
}


/**
 * stores authentications details in keychain
 */
function storeAuthentication(jwt, keychain) {

  if (jwt?.refresh_token) {

    const tokenInfos = {
      ...jwt,
      expireTime: Date.now() + (jwt?.expires_in * 1000 || OAUTH_DEFAULT_EXPIRE_SECONDS * 1000)
    };

    if (keychain) {
      return keychain.setItem(
        OAUTH_KEYCHAIN_NAME,
        jwt.refresh_token
      )
        .then(() => tokenInfos)
        //if storing fails return token anyway
        .catch(() => tokenInfos);
    }

    return Promise.resolve(tokenInfos);
  }

  return Promise.reject(false);
}


// -----------------------------------------------------------------------------
// OAUTH SERVICE
// -----------------------------------------------------------------------------

/**
 * Factory function which creates a oauth service with given
 * depedencies
 */
export function createOAuthService(
  localConfig,
  fetchService,
  keyChain,
  logger,
  netInfo,
  remoteConfig,
  queryString,
  sessionStorageService
) {

  /**
   * ref to current jwt token
   */
  let jsonWebToken = undefined;

  const appConfig = localConfig.getLocalConfig();
  const isDevelop = appConfig.BUNDLER.BUILD_MODE === appConfig.BUNDLER.BUILD_MODES.DEVELOPMENT;


  // ---------------------------------------------------------------------------
  // PRIVATE METHODS
  // ---------------------------------------------------------------------------

  /**
   * Creates a URL to start the authorization process
   */
  function fetchAuthorizeUrl(resellerId, isAutoLogin = false) {
console.log("resellerIdresellerIdresellerIdresellerIdresellerIdresellerId",resellerId)
    //get remote config to retrieve oauth parameters
    return remoteConfig.fetchRemoteConfig(resellerId)
      .then(rConfig => {
        //Standard parameters
        let params = [
          "client_id=" + rConfig.OAUTH_CLIENT_ID,
          "response_type=code",
          "scope=" + encodeURIComponent(rConfig.OAUTH_SCOPE),
          "redirect_uri=" + encodeURIComponent(isDevelop ? rConfig.URL_OAUTH_REDIRECT : rConfig.URL_OAUTH_AUTHAPP)
        ];

        //reseller dependent parameters
        params = addSpecialConfig(rConfig, params);

        let channelId = rConfig.CHANNEL_ID;
        if (channelId) {
          params.push("channelid=" + channelId);
        }

        if (isAutoLogin && rConfig.URL_OAUTH_AUTOLOGIN) {
          logger.info(`${TAG} Using autoLogin URL for authorization ${rConfig.URL_OAUTH_AUTOLOGIN}`);
          return rConfig.URL_OAUTH_AUTOLOGIN + "?" + params.join("&");
        }

        return rConfig.URL_OAUTH_AUTHORIZATION + "?" + params.join("&");
      })
      .catch(() => {
        //we can't return a authorize url if no reseller config is available
        logger.error(`${TAG} No reseller config to fetch authorize URL for`);
        throw new OAuthError(2);
      });
  }


  /**
   * async function to retrieve a token
   */
  function fetchToken(resellerId) {

    // -----------------------------------------------------------------------------
    // TOKEN ALREADY FETCHED
    // -----------------------------------------------------------------------------

    if (jsonWebToken) {
      //expire check

      if (Date.now() + OAUTH_EXPIRE_THRESHOLD < jsonWebToken.expireTime) {
        return Promise.resolve(jsonWebToken);
      }

      logger.warn(`${TAG} access_token expired; attempting refetch`);
    }

    // -----------------------------------------------------------------------------
    // TOKEN FROM CODE
    // -----------------------------------------------------------------------------

    return netInfo.fetchIsConnected()
      .then(online => {

        //there is no token and no network -> bail out
        if (!online) {
          logger.error(`${TAG} Unable to fetch token without network connectivity`);
          return Promise.reject(new OAuthError(1));
        }

        //get remote config to retrieve oauth parameters
        return remoteConfig.fetchRemoteConfig(resellerId)
          .then(rConfig => {

            //A login code takes prio over refresh tokens
            const code = queryString.getQueryParameter("code");
            if (code) {
              return fetchTokenFromCode(code, fetchService.fetch, rConfig, isDevelop)
                .then(jwt => storeAuthentication(jwt, keyChain)

                  //set the token in the module variable to make further retrievals faster
                  .then(tokenInfos => {
                    jsonWebToken = tokenInfos;
                    return tokenInfos;
                  }))

                .catch((err) => {
                  logger.error(`${TAG} Unable to fetch token with given code ${code} => ${err}`);
                  return getDemoToken(rConfig, logger);
                }).finally(() => queryString.removeQueryParameter("code"));
            }

            // -----------------------------------------------------------------------------
            // TOKEN FROM SESSION STORE (AUTH APP)
            // -----------------------------------------------------------------------------

            if (sessionStorageService) {
              const appConfig = localConfig.getLocalConfig();
              const jwtString = sessionStorageService
                .getItem(appConfig.CONSTANTS.O_AUTH.KEY_AUTH_APP_TOKEN_DATA);
              if (jwtString) {
                let jwt;
                try {
                  jwt = JSON.parse(jwtString);
                } catch (e) {
                  logger.error("Could not parse session stored token ->" + e)
                }

                return storeAuthentication(jwt, keyChain)
                  .then(tokenInfos => {
                    jsonWebToken = tokenInfos;
                    return tokenInfos;
                  })
                  .catch(() => {
                    return jwt || getDemoToken(rConfig, logger);
                  })
                  .finally(() => {
                    sessionStorageService
                      .removeItem(appConfig.CONSTANTS.O_AUTH.KEY_AUTH_APP_TOKEN_DATA);
                  });
              }
            }

            // -----------------------------------------------------------------------------
            // TOKEN FROM REFRESH TOKEN
            // -----------------------------------------------------------------------------

            if (keyChain) {

              //check keychain for refresh token
              return keyChain.getItem(OAUTH_KEYCHAIN_NAME)
                .then(rToken => {

                  if (rToken) {

                    //try fetching a new refresh token and a access token
                    return fetchTokenFromRefresh(rConfig, fetchService.fetch, rToken)
                      .then(jwt => storeAuthentication(jwt, keyChain)

                        //set the token in the module variable to make further retrievals faster
                        .then(tokenInfos => jsonWebToken = tokenInfos))
                      .catch(() => {

                        logger.error(`${TAG} refresh_token is invalid`);

                        //refresh token is invalid
                        return keyChain.removeItem(OAUTH_KEYCHAIN_NAME)

                          //return demo token
                          .then(() => getDemoToken(rConfig, logger))
                          .catch(() => getDemoToken(rConfig, logger));
                      });
                  }

                  //return demo token
                  logger.info(`${TAG} No token information stored to fetch token`);
                  return getDemoToken(rConfig, logger);
                })
                .catch(() => {
                  logger.error(`${TAG} keychain error`);
                  return getDemoToken(rConfig, logger);
                });
            }

            return getDemoToken(rConfig, logger);

          })
          .catch(error => {

            //pass through for OAuthError errors
            if (error instanceof OAuthError) {
              logger.error(`${TAG} Unable to fetch token ${error}`);
              throw error;
            }

            //we can't return a demo token if no reseller config is available
            logger.error(`${TAG} no reseller config to fetch anything`);
            throw new OAuthError(2);
          });

      });
  }


  /**
   * revoke refresh token on oauth server
   */
  function revokeToken(resellerId) {

    return netInfo.fetchIsConnected()
      .then(online => {

        //to revoke a token we need network access
        if (!online) {
          logger.error(`${TAG} Unable to revoke token without network connectivity`);
          return Promise.reject(new OAuthError(1));
        }

        return remoteConfig.fetchRemoteConfig(resellerId)
          .then(rConfig => {

            return fetchToken()
              .then(jwt => {

                //don't revoke a demo token or revoke without
                //refresh token or revoke url
                if (jwt.isDemo || !jwt.refresh_token || !rConfig.URL_OAUTH_REVOKETOKEN) {
                  return Promise.reject(new OAuthError(4));
                }

                // Set revoke options
                let form = [
                  "client_id=" + encodeURIComponent(rConfig.OAUTH_CLIENT_ID),
                  "token_type=refresh_token",
                  "token=" + encodeURIComponent(jwt.refresh_token)
                ].join("&");

                return requestAuthentication(fetchService.fetch, form, rConfig.URL_OAUTH_REVOKETOKEN);

              })
          });
      })
      .catch(error => {
        logger.error(`${TAG} error revoking token ${error}`);
      })
      .finally(() => {

        //invalidate token
        jsonWebToken = null;

        //delete token from keychain
        return keyChain?.removeItem(OAUTH_KEYCHAIN_NAME);
      });
  }


  // ---------------------------------------------------------------------------
  // PUBLIC API
  // ---------------------------------------------------------------------------

  return {
    fetchToken,
    fetchAuthorizeUrl,
    revokeToken
  };

}
